<template>
  <!--
    Per-account summary for the Portfolio > Earnings tab.
    Distinct from the dashboard hero's InvestmentTypeCard: here the design puts
    the account label and its total inline on one row, on the theme-aware canvas
    surface. Desktop ("card") shows the sublines always; mobile ("row") is a
    tappable pill that expands to reveal them.
  -->

  <!-- Mobile: tappable pill row. -->
  <div v-if="layout === 'row'" class="rounded-[20px] bg-bg-canvas px-3 py-2.5">
    <button type="button" class="flex w-full items-center justify-between gap-3" @click="toggle()">
      <span class="font-inter text-md font-normal text-strongest">{{ group.label }}</span>
      <span class="flex items-center gap-3">
        <span class="font-league text-lg font-normal text-strongest">{{ group.value }}</span>
        <Button variant="paper-primary" size="sm" aria-label="View breakdown">
          <template #icon>
            <Icon
              name="ChevronDown"
              class="h-4 w-4 transition-transform"
              :class="isExpanded ? 'rotate-180' : ''"
              :stroke-width="2"
            />
          </template>
        </Button>
      </span>
    </button>

    <div v-if="isExpanded" class="mt-3 space-y-1">
      <div
        v-for="line in group.subLines"
        :key="line.label"
        class="flex items-center justify-between gap-4"
      >
        <p class="font-inter text-sm font-light text-light">{{ line.label }}</p>
        <p class="font-inter text-sm font-light text-strongest">{{ line.value }}</p>
      </div>
    </div>
  </div>

  <!-- Desktop: full card with the sublines always visible. -->
  <div v-else class="flex h-full flex-col gap-3 rounded-[20px] bg-bg-canvas p-5">
    <div class="flex items-center justify-between gap-4">
      <p class="font-inter text-base font-medium text-strongest">{{ group.label }}</p>
      <p class="font-league text-xl font-normal text-strongest">{{ group.value }}</p>
    </div>

    <div class="space-y-1">
      <div
        v-for="line in group.subLines"
        :key="line.label"
        class="flex items-center justify-between gap-4"
      >
        <p class="font-inter text-sm font-light text-light">{{ line.label }}</p>
        <p class="font-inter text-sm font-light text-strongest">{{ line.value }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { Button, Icon } from "@/components";

defineProps({
  // One entry from useInvestmentStore's getPortfolioAccountCards():
  // { key, label, value, subLines: [{ label, value }], hasMonthlyPayments }.
  group: { type: Object, required: true },
  // "card" = desktop grid item. "row" = mobile tap-to-expand pill.
  layout: {
    type: String,
    default: "card",
    validator: (value) => ["card", "row"].includes(value),
  },
});

const isExpanded = ref(false);
function toggle() {
  isExpanded.value = !isExpanded.value;
}
</script>
